import { inject, Injectable, Service } from "@angular/core";
import { environment } from "environments/environment";
import { BasicHttpClientService } from "./basic-http-client.service";
import { Params } from "@angular/router";
import { CustomizationTemplateDTO } from "../models/customization-template.models";
import { PagedResponse } from "../models/paged-response.model";
import {
  MessageAttachmentResponseDto,
  ChangeCrProductTemplateDTI,
  CommercialApprovalApprovedProductDTI,
  CommercialApprovalApprovedVariantDTI,
  CommercialApprovalDTI,
  CommercialApprovalResponseDto,
  ConfiguratorStateDto,
  CustomizationCreateRequestDTO,
  CustomizationRequestDTO,
  CustomizationRequestDetailDTO,
  CustomizationRequestFormVm,
  CustomizedProductAdditionDTO,
  CustomizedProductDTO,
  CustomizedProductDetailDTO,
  CustomizedProductVM,
  GraphicalApprovalApprovedProductDTI,
  GraphicalApprovalDTI,
  GraphicalApprovalResponseDto,
  RequesterApprovalApprovedProductDTI,
  RequesterApprovalApprovedVariantDTI,
  RequesterApprovalDTI,
  RequesterApprovalResponseDto,
  updateCrProductConfiguratorStateDTI,
  BulkCustomizedProductSizesUpdateRequest,
  BulkCustomizedProductSizesUpdateResponse,
  BulkSizeUpdatePlayerRequest,
  BulkSizeUpdateProductRequest,
  BulkSizeUpdateSizeRequest,
  SizeGroupLabel,
  CrReportExcelParams,
} from "../models/customization-request.models";
import { PlayersImportPreviewDto } from "../models/cr-excel-import.models";
import { SizeGroupTypeEnum } from "app/back-office/shared/enum/size-group-type.enum.model";
import { ShopContextStore } from "app/shared/state/shop-context.store";
import { RequestTimelineResponseDto } from "../models/customization-request-timeline.models";
import { forkJoin, Observable, of } from "rxjs";
import {
  TemporaryFileReason,
  TemporaryFileTypeEnum,
} from "../enums/temporary-file-type.enum";
import { map, switchMap, take } from "rxjs/operators";
import { UtilityFunctions } from "../utilities/functions.models";
import {
  PlayerCustomizationDTO,
  PlayerCustomizationInfoGridRowVM,
  PlayerCustomizationVM,
  PlayerInfoGridRowVM,
} from "app/back-office/shared/models/basic-kustom/player-customization.model";
import { HttpBasicRequest, HttpBasicRequestOptions } from "../models/http-basic-request.model";
import { CustomizationFontDTO } from "../models/customization-font.model";
import { VariantDTO } from "app/back-office/shared/models/products/variant.models";
import { OrderTypeEnum } from "../enums/order-type.enum.model";
import {
  EXPIVI_CATALOG_IDS_TO_EXCLUDE,
  EXPIVI_DEFAULT_BASE64,
} from "app/shop/shared/constants/shared.constants";
import { CustomizationRequestStatusEnum } from "../enums/customization-request-status.enum";
import { ProductProjectTypeEnum } from "../enums/product-project-type.enum";

@Service()
export class CustomizationRequestService {
  private readonly baseUrl: string = environment.urls.apiUrl.url;
  private readonly _shopContext = inject(ShopContextStore);

  private readonly _httpClient = inject(BasicHttpClientService);

  getProductsVariants(
    channelId: number,
    productIds: number[],
    loaderId?: string,
  ): Observable<VariantDTO[]> {
    const requests: HttpBasicRequest[] = productIds.map((p) => {
      return new HttpBasicRequest({
        method: "get",
        options: {
          params: {
            storeId: this._shopContext.currentShop().id,
            maxResultCount: 0,
            skipCount: 0,
            channelId,
            productId: p,
            priceListType: OrderTypeEnum.PreOrder,
          },
        },
        url: `${this.baseUrl}/variants`,
      });
    });
    return this._httpClient.forkJoin(requests, loaderId).pipe(
      map((data: PagedResponse<VariantDTO>[]) => {
        var result = [];
        data.forEach((el) => {
          result = result.concat(el?.items || []);
        });
        return result;
      }),
    );
  }

  getExpiviTemplate(
    loaderId: string,
    productId: number,
    channelId?: number,
    storeId?: number,
  ) {
    const params: Params = {
      productId: productId.toString(),
      storeId: storeId ?? this._shopContext.currentShop().id.toString(),
      channelId:
        channelId ??
        this._shopContext.currentActivatedChannel().channel.id.toString(),
    };

    return this._httpClient.get<CustomizationTemplateDTO[]>(
      `${this.baseUrl}/customization-requests/templates`,
      { params },
      loaderId,
    );
  }

  getCustomizationRequest(
    loaderId: string,
    skipCount: number,
    maxResultCount: number,
    sorting: string = null,
    channelId: number,
    storeId: number,
    searchString: string,
    customerId?: number,
    pointOfSaleId?: number,
    status?: CustomizationRequestStatusEnum,
    commercialApprovalByUserId?: number,
    createdByUserId?: number,
    designerUserId?: number,
    customerRequestingUserId?: number,
    agencyRequestingUserId?: number,
    haveDesignerNotification?: boolean,
    agencyId?: number,
    projectType?: ProductProjectTypeEnum,
  ) {
    const params: Params = {
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString,
    };

    if (sorting) params["sorting"] = sorting;
    if (channelId) params["channelId"] = channelId.toString();
    if (storeId) params["storeId"] = storeId.toString();
    if (customerId) params["customerId"] = customerId.toString();
    if (pointOfSaleId) params["pointOfSaleId"] = pointOfSaleId.toString();
    if (status) params["status"] = status;
    if (commercialApprovalByUserId)
      params["commercialApprovalByUserId"] =
        commercialApprovalByUserId.toString();
    if (createdByUserId) params["createdByUserId"] = createdByUserId.toString();
    if (designerUserId) params["designerUserId"] = designerUserId.toString();
    if (customerRequestingUserId)
      params["customerRequestingUserId"] = customerRequestingUserId.toString();
    if (agencyRequestingUserId)
      params["agencyRequestingUserId"] = agencyRequestingUserId.toString();
    if (
      haveDesignerNotification !== undefined &&
      haveDesignerNotification !== null
    )
      params["haveDesignerNotification"] = haveDesignerNotification.toString();
    if (agencyId) params["agencyId"] = agencyId.toString();
    if (projectType !== undefined && projectType !== null)
      params["projectType"] = projectType.toString();
    return this._httpClient.get<PagedResponse<CustomizationRequestDTO>>(
      `${this.baseUrl}/customization-requests`,
      { params },
      loaderId,
    );
  }

  clearNotifications(loaderId: string, customRequestId: number) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/clear-notifications/${customRequestId}`,
      null,
      null,
      loaderId
    );
  }

  createCustomizationRequest(
    loaderId: string,
    productId: number,
    crForm: CustomizationRequestFormVm,
    thumbnail: string = null,
    configuratorState: ConfiguratorStateDto,
    configuratorStateJson: string = null,
  ) {
    // Normalizza crMessageAttachment in array
    const attachments = crForm.crMessageAttachment
      ? Array.isArray(crForm.crMessageAttachment)
        ? crForm.crMessageAttachment
        : [crForm.crMessageAttachment]
      : [];

    return new Observable((observer) => {
      if (attachments.length > 0) {
        const uploadRequests = attachments.map((file) => {
          const formData = new FormData();
          formData.append("file", file);
          return this._httpClient
            .post(
              `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`,
              formData,
              null,
              loaderId,
            )
            .pipe(take(1));
        });

        forkJoin(uploadRequests).subscribe({
          next: (uploadIds: string[]) => {
            observer.next(uploadIds);
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
      } else {
        observer.next(null);
        observer.complete();
      }
    }).pipe(
      switchMap((imageAttachmentUploadIds: string[]) => {
        const body: CustomizationCreateRequestDTO =
          new CustomizationCreateRequestDTO({
            channelId:
              this._shopContext.currentActivatedChannel().channel.id,
            pointOfSaleId: crForm.pointOfSaleId ?? this._shopContext.currentPos().id,
            storeId: this._shopContext.currentShop().id,
            title: crForm.crName,
            firstProduct: this.genProductAddition(
              productId,
              crForm,
              thumbnail,
              configuratorState,
              configuratorStateJson,
              imageAttachmentUploadIds,
            ),
          });

        if (crForm.customerRequesterId)
          body.customerRequestingUserId = crForm.customerRequesterId;
        if (crForm.agencyRequesterId)
          body.agencyRequestingUserId = crForm.agencyRequesterId;
        if (!!body?.firstProduct && !body.firstProduct.temporaryAttachmentUploadIds) {
          body.firstProduct.temporaryAttachmentUploadIds = [];
        }

        return this._httpClient.post(
          `${this.baseUrl}/customization-requests`,
          body,
          null,
          loaderId,
        );
      }),
    );
  }

  // addProductToCustomizationRequest(
  //   loaderId: string,
  //   customRequestId: number,
  //   productId: number,
  //   crForm: CustomizationRequestFormVm,
  //   thumbnail: string = null,
  //   configuratorState: ConfiguratorStateDto,
  //   configuratorStateJson: string,
  // ) {

  //   const body: CustomizedProductAdditionDTO = this.genProductAddition(productId, crForm, thumbnail, configuratorState, configuratorStateJson);

  //   const formData = new FormData();
  //   if (crForm?.crMessageAttachment) {
  //     formData.append('file', crForm.crMessageAttachment);
  //   }

  //   return new Observable(observer => {
  //     if (crForm.crMessageAttachment) {
  //       this._httpClient.post(`${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`, formData, null, loaderId).subscribe(res => {
  //         body['temporaryAttachmentUploadId'] = res;
  //         observer.next();
  //       });
  //     } else {
  //       observer.next();
  //     }
  //   }).pipe(
  //     switchMap(() => this._httpClient.post(`${this.baseUrl}/customization-requests/${customRequestId}/products`, body, null, loaderId)
  //     ));
  // }

  addProductToCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    productId: number,
    crForm: CustomizationRequestFormVm,
    thumbnail: string = null,
    configuratorState: ConfiguratorStateDto,
    configuratorStateJson: string,
  ) {
    // Normalizza crMessageAttachment in array
    const attachments = crForm.crMessageAttachment
      ? Array.isArray(crForm.crMessageAttachment)
        ? crForm.crMessageAttachment
        : [crForm.crMessageAttachment]
      : [];

    return new Observable((observer) => {
      if (attachments.length > 0) {
        const uploadRequests = attachments.map((file) => {
          const formData = new FormData();
          formData.append("file", file);
          return this._httpClient
            .post(
              `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`,
              formData,
              null,
              loaderId,
            )
            .pipe(take(1));
        });

        forkJoin(uploadRequests).subscribe({
          next: (uploadIds: string[]) => {
            observer.next(uploadIds);
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
      } else {
        observer.next(null);
        observer.complete();
      }
    }).pipe(
      switchMap((imageAttachmentUploadIds: string[]) => {
        const body: CustomizedProductAdditionDTO = this.genProductAddition(
          productId,
          crForm,
          thumbnail,
          configuratorState,
          configuratorStateJson,
          imageAttachmentUploadIds,
        );
        if (!!body && !body.temporaryAttachmentUploadIds) {
          body.temporaryAttachmentUploadIds = [];
        }
        return this._httpClient.post(
          `${this.baseUrl}/customization-requests/${customRequestId}/products`,
          body,
          null,
          loaderId,
        );
      }),
    );
  }

  duplicateProduct(
    loaderId: string,
    customRequestId: number,
    body: CustomizedProductAdditionDTO,
  ) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/products`,
      body,
      null,
      loaderId,
    );
  }

  changeTemplateOfCustomizationRequestProduct(
    loaderId: string,
    customRequestId: number,
    productId: number,
    newTemplateId: number,
    thumbnail: string = null,
    configuratorState: ConfiguratorStateDto,
    configuratorStateJson: string = null,
  ) {
    const body: ChangeCrProductTemplateDTI = new ChangeCrProductTemplateDTI({
      configuratorState: configuratorState,
      configuratorThumbnail: thumbnail.replace("data:", ""),
      newTemplateId: newTemplateId,
      configuratorStateJson: configuratorStateJson,
    });

    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/products/${productId}/template`,
      body,
      null,
      loaderId,
    );
  }

  updateConfigurationStateOfCustomizationRequestProduct(
    loaderId: string,
    customRequestId: number,
    productId: number,
    thumbnail: string = null,
    configuratorState: ConfiguratorStateDto,
    configuratorStateJson: string = null,
  ) {
    const body: updateCrProductConfiguratorStateDTI =
      new updateCrProductConfiguratorStateDTI({
        configuratorState: configuratorState,
        configuratorThumbnail: thumbnail.replace("data:", ""),
        configuratorStateJson: configuratorStateJson,
      });

    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/products/${productId}`,
      body,
      null,
      loaderId,
    );
  }

  genProductAddition(
    productId: number,
    crForm: CustomizationRequestFormVm,
    thumbnail: string = null,
    configuratorState: ConfiguratorStateDto,
    configuratorStateJson: string = null,
    imageAttachmentUploadId: string[] = null,
  ) {
    let result: CustomizedProductAdditionDTO = null;

    let templateId = crForm.crMessage
      ? crForm.defaultPreset.expiviId
      : crForm.expiviPresetCardSelected.expiviId;

    result = new CustomizedProductAdditionDTO({
      isNewConfigRequest: !!crForm.crMessage,
      newConfigRequestMessageContent: crForm.crMessage || null,
      configuratorThumbnail:
        thumbnail && thumbnail.trim()
          ? thumbnail.replace("data:", "")
          : EXPIVI_DEFAULT_BASE64,
      templateId: templateId.toString(),
      productId,
      fontId: crForm.fontId,
      comments: crForm.comments,
      configuratorState: configuratorState,
      temporaryAttachmentUploadIds: imageAttachmentUploadId,
      configuratorStateJson: configuratorStateJson,
    });

    return result;
  }

  getCustomizationRequestDetail(loaderId: string, customRequestId: number) {
    return this._httpClient.get<CustomizationRequestDetailDTO>(
      `${this.baseUrl}/customization-requests/${customRequestId}/detail`,
      null,
      loaderId,
    );
  }

  getFonts(loaderId: string): Observable<CustomizationFontDTO[]> {
    return this._httpClient.get<CustomizationFontDTO[]>(
      `${this.baseUrl}/customization-requests/fonts`,
      {
        params: {
          skipCount: 0,
          maxResultCount: 1000,
        },
      },
      loaderId,
    );
  }

  deleteCustomizationRequest(loaderId: string, customRequestId: number) {
    return this._httpClient.delete(
      `${this.baseUrl}/customization-requests/${customRequestId}`,
      null,
      loaderId,
    );
  }

  rejectCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    rejectionReason: string,
  ) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/rejection`,
      { rejectionMessageContent: rejectionReason },
      null,
      loaderId,
    );
  }

  reAssignDesignerOfCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    designerId: number,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/designer`,
      designerId,
      null,
      loaderId,
    );
  }

  updateTitleOfCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    title: string,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/detail`,
      { newRequestTitle: title },
      null,
      loaderId,
    );
  }

  changePointOfSaleOfCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    pointOfSaleId: number,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/point-of-sale`,
      pointOfSaleId,
      null,
      loaderId,
    );
  }

  reAssignCustomerRequesterOfCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    customerRequesterId: number,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/customer-requester/${customerRequesterId}`,
      {},
      null,
      loaderId,
    );
  }

  reAssignAgencyRequesterOfCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    agencyRequesterId: number,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${customRequestId}/agency-requester/${agencyRequesterId}`,
      {},
      null,
      loaderId,
    );
  }

  lockCustomizationRequest(loaderId: string, customRequestId: number) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/requester-lock/apply`,
      null,
      null,
      loaderId,
    );
  }

  unlockCustomizationRequest(loaderId: string, customRequestId: number) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/requester-lock/remove`,
      null,
      null,
      loaderId,
    );
  }

  getCustomizationRequestProducts(loaderId: string, customRequestId: number) {
    return this._httpClient.get<CustomizedProductDTO[]>(
      `${this.baseUrl}/customization-requests/${customRequestId}/products`,
      null,
      loaderId,
    );
  }

  getCustomizationRequestProductDetail(
    loaderId: string,
    customRequestId: number,
    productId: number,
  ) {
    return this._httpClient
      .get<CustomizedProductDetailDTO>(
        `${this.baseUrl}/customization-requests/${customRequestId}/products/${productId}`,
        null,
        loaderId,
      )
      .pipe(
        map((data) => {
          data.customizedSizes = data.customizedSizes.sort(
            (a, b) => a.sizeOrder - b.sizeOrder,
          );
          return data;
        }),
      );
  }

  getMultipleProductDetails(
    loaderId: string,
    customRequestId: number,
    productIds: number[],
  ): Observable<CustomizedProductDetailDTO[]> {
    const requests: HttpBasicRequest[] = productIds.map(
      (productId) =>
        new HttpBasicRequest({
          method: "get",
          url: `${this.baseUrl}/customization-requests/${customRequestId}/products/${productId}`,
        }),
    );
    return this._httpClient
      .forkJoin<CustomizedProductDetailDTO>(requests, loaderId)
      .pipe(
        map((data: CustomizedProductDetailDTO[]) => {
          return (data || []).map((d) => {
            if (d?.customizedSizes) {
              d.customizedSizes = d.customizedSizes.sort(
                (a, b) => a.sizeOrder - b.sizeOrder,
              );
            }
            return d;
          });
        }),
      );
  }

  // getCustomizationRequestPlayersByProductsIds(
  //   loaderId: string,
  //   customRequestId: number,
  //   productIds: number[],
  // ): Observable<{ dataRows: PlayerInfoGridRowVM[]; fontId: number }> {
  //   var requests: HttpBasicRequest[] = productIds.map(
  //     (x) =>
  //       new HttpBasicRequest({
  //         method: "get",
  //         url: `${this.baseUrl}/customization-requests/${customRequestId}/products/${x}`,
  //       }),
  //   );
  //   return this._httpClient
  //     .forkJoin<CustomizedProductDetailDTO>(requests, loaderId)
  //     .pipe(
  //       map((data: CustomizedProductDetailDTO[]) => {
  //         var result: { dataRows: PlayerInfoGridRowVM[]; fontId: number } = {
  //           fontId: data?.[0]?.fontId ?? null,
  //           dataRows: [],
  //         };
  //
  //         (data || []).forEach((prod) => {
  //           const customizedSizes = prod.customizedSizes ?? [];
  //           if (customizedSizes.length === 0) return;
  //
  //           customizedSizes.forEach((cs) => {
  //             const rowsForSize: PlayerInfoGridRowVM[] = [];
  //             const playerCustomizations = cs.playerCustomizations ?? [];
  //
  //             // Raggruppa player identici (stesso number + displayName): una riga per gruppo con quantity = conteggio
  //             const groupByKey = new Map<string, { pc: { number?: string; displayName?: string }; count: number }>();
  //             playerCustomizations.forEach((pc) => {
  //               const key = `${pc.number ?? ""}|${pc.displayName ?? ""}`;
  //               const existing = groupByKey.get(key);
  //               if (existing) {
  //                 existing.count++;
  //               } else {
  //                 groupByKey.set(key, { pc, count: 1 });
  //               }
  //             });
  //             groupByKey.forEach(({ pc, count }) => {
  //               rowsForSize.push(
  //                 new PlayerInfoGridRowVM({
  //                   styleCode: prod.productSku,
  //                   productComments: prod.comments,
  //                   sizeName: cs.sizeName,
  //                   sizeOrder: cs.sizeOrder,
  //                   quantity: count,
  //                   numbers: pc.number ?? "",
  //                   shirtNames: pc.displayName ?? "",
  //                 }),
  //               );
  //             });
  //
  //             // Una riga per le quantità senza personalizzazione (taglia stessa, totale - custom)
  //             const noCustomQty = cs.requestedQuantity - playerCustomizations.length;
  //             if (noCustomQty > 0) {
  //               rowsForSize.push(
  //                 new PlayerInfoGridRowVM({
  //                   styleCode: prod.productSku,
  //                   productComments: prod.comments,
  //                   sizeName: cs.sizeName,
  //                   sizeOrder: cs.sizeOrder,
  //                   quantity: noCustomQty,
  //                   numbers: " - ",
  //                   shirtNames: " - ",
  //                 }),
  //               );
  //             }
  //
  //             result.dataRows.push(...rowsForSize);
  //           });
  //         });
  //
  //         // Dedup per chiave (stesso prodotto/taglia/numero/nome = una sola riga)
  //         const seen = new Map<string, PlayerInfoGridRowVM>();
  //         result.dataRows = result.dataRows.filter((row) => {
  //           const key = `${row.styleCode}|${row.sizeName}|${row.sizeOrder}|${row.numbers}|${row.shirtNames}`;
  //           if (seen.has(key)) return false;
  //           seen.set(key, row);
  //           return true;
  //         });
  //
  //         return result;
  //       }),
  //     );
  // }

  getCustomizationRequestPlayersByProductsIds(
    loaderId: string,
    customRequestId: number,
  ): Observable<{ dataRows: PlayerInfoGridRowVM[]; fontId: number }> {
    return this._httpClient
      .get(
        `${this.baseUrl}/customization-requests/${customRequestId}/players-table`,
        null,
        loaderId,
      )
      .pipe(
        map((data: any[]) => {
          const dataRows: PlayerInfoGridRowVM[] = (data || []).map(
            (item) =>
              new PlayerInfoGridRowVM({
                styleCode: item.styleCode,
                productComments: item.crStyleName,
                sizeName: item.sizeName,
                sizeOrder: 0,
                quantity: item.quantity,
                numbers: item.number ?? "",
                shirtNames: item.jerseysText ?? "",
              }),
          );
          return { dataRows, fontId: null };
        }),
      );
  }

  deleteCustomizationRequestProduct(
    loaderId: string,
    customRequestId: number,
    productId: number,
  ) {
    return this._httpClient.delete(
      `${this.baseUrl}/customization-requests/${customRequestId}/products/${productId}`,
      null,
      loaderId,
    );
  }

  getRequestingUserApproval(
    loaderId: string,
    customRequestId: number,
  ): Observable<RequesterApprovalResponseDto> {
    return this._httpClient.get<RequesterApprovalResponseDto>(
      `${this.baseUrl}/customization-requests/${customRequestId}/requesting-user-approval`,
      null,
      loaderId,
    );
  }

  /** Costruisce il body per l'approvazione requester (riusabile per POST e per log) */
  buildRequesterApprovalBody(
    crProducts: CustomizedProductVM[],
    playerCustomizations: PlayerCustomizationVM[],
    fontId: number = null,
  ): RequesterApprovalDTI {
    return new RequesterApprovalDTI({
      approvedProducts: crProducts.map(
        (product) =>
          new RequesterApprovalApprovedProductDTI({
            fontId: fontId,
            approvedVariants: product.sizes
              .map((size) => {
                let players: PlayerCustomizationDTO[] = [];
                let customizedQty = 0;

                (playerCustomizations || []).forEach((player) => {
                  // Cerca il prodotto corrispondente (ora player.products ha UN SOLO elemento)
                  const prod = player.products?.find(
                    (productPlayer) =>
                      productPlayer.id === product.id &&
                      productPlayer.selectedSize?.sizeId === size.sizeId,
                  );

                  if (prod?.selectedSize) {
                    customizedQty += prod.selectedSize.requestedQuantity || 0;
                    for (
                      let j = 0;
                      j < prod.selectedSize.requestedQuantity;
                      j++
                    ) {
                      players.push(
                        new PlayerCustomizationDTO({
                          displayName: player.displayName,
                          firstName: player.firstName,
                          lastName: player.lastName,
                          number: player.number,
                          id: player.id,
                        }),
                      );
                    }
                  }
                });

                return new RequesterApprovalApprovedVariantDTI({
                  approvedQuantity:
                    (size.requestedQuantity || 0) + customizedQty,
                  variantId: size.variantId,
                  playerCustomizations: players,
                });
              })
              .filter((size) => size.approvedQuantity > 0),
            customizedProductId: product.id,
          }),
      ),
    });
  }

  approveCustomizationRequestAsRequester(
    loaderId: string,
    customRequestId: number,
    crProducts: CustomizedProductVM[],
    playerCustomizations: PlayerCustomizationVM[],
    fontId: number = null,
  ) {
    const body = this.buildRequesterApprovalBody(
      crProducts,
      playerCustomizations,
      fontId,
    );
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/requesting-user-approval`,
      body,
      null,
      loaderId,
    );
  }

  // ============================================================
  // BULK SIZE UPDATE – POST /products/sizes
  // ============================================================

  /**
   * Aggiorna in bulk le size (e relativi player) di una customization request.
   * Usa il nuovo endpoint best-effort: ogni size viene processata indipendentemente.
   *
   * @param loaderId   ID loader per lo spinner
   * @param requestId  ID della customization request
   * @param playersMap Mappa { customizedProductId → player[] } dal componente UI
   * @returns Observable con summary + risultati per-item
   */
  bulkUpdateProductSizes(
    loaderId: string,
    requestId: number,
    crProducts: CustomizedProductVM[],
    playersMap: { [customizedProductId: number]: PlayerCustomizationVM[] },
  ): Observable<BulkCustomizedProductSizesUpdateResponse> {
    const payload = this.buildBulkSizePayloadFromProducts(crProducts, playersMap);
    return this._httpClient.post<BulkCustomizedProductSizesUpdateResponse>(
      `${this.baseUrl}/customization-requests/${requestId}/products/sizes`,
      payload,
      null,
      loaderId,
    );
  }

  /**
   * Costruisce il payload per la POST /products/sizes a partire dalla mappa player.
   *
   * Differenza chiave rispetto al vecchio codice:
   * - I player NON vengono duplicati per la quantità (requestedQuantity è un campo numerico separato)
   * - La size può essere INSERT (id nullo) o UPDATE (id valorizzato)
   */
  private buildBulkSizeUpdatePayload(
    playersMap: { [customizedProductId: number]: PlayerCustomizationVM[] },
  ): BulkCustomizedProductSizesUpdateRequest {
    const productIds = Object.keys(playersMap)
      .map((k) => parseInt(k, 10))
      .filter((id) => !isNaN(id));

    const products: BulkSizeUpdateProductRequest[] = [];

    for (const productId of productIds) {
      const playersOfProduct = playersMap[productId] ?? [];

      // Filtro player validi: devono avere una size selezionata con quantità > 0
      const validPlayers = playersOfProduct.filter((player) => {
        const selectedSize = player.products?.[0]?.selectedSize;
        if (!selectedSize || !(selectedSize.requestedQuantity > 0)) return false;
        const hasExistingId = selectedSize.id != null && Number(selectedSize.id) > 0;
        const hasCatalogSizeId = selectedSize.sizeId != null;
        return hasExistingId || hasCatalogSizeId;
      });

      if (validPlayers.length === 0) continue;

      // Raggruppo i player per size usando una chiave composta:
      // - UPDATE → customizedProductSizeId (selectedSize.id)
      // - INSERT → sizeId catalogo (selectedSize.sizeId) con prefisso "new_" per evitare collisioni
      const sizeMap = new Map<string, {
        selectedSize: typeof validPlayers[0]['products'][0]['selectedSize'];
        totalRequestedQuantity: number;
        players: BulkSizeUpdatePlayerRequest[];
      }>();

      for (const player of validPlayers) {
        const selectedSize = player.products[0].selectedSize;
        const isUpdate = selectedSize.id != null && Number(selectedSize.id) > 0;
        const sizeKey = isUpdate
          ? String(selectedSize.id)
          : `new_${selectedSize.sizeId}`;

        if (!sizeMap.has(sizeKey)) {
          sizeMap.set(sizeKey, { selectedSize, totalRequestedQuantity: 0, players: [] });
        }

        const entry = sizeMap.get(sizeKey)!;
        const qty = selectedSize.requestedQuantity || 0;
        entry.totalRequestedQuantity += qty;

        // Duplica il player N volte in base alla sua quantità
        for (let i = 0; i < qty; i++) {
          entry.players.push(
            new BulkSizeUpdatePlayerRequest({
              displayName: player.displayName ?? '',
              firstName: player.firstName ?? undefined,
              lastName: player.lastName ?? undefined,
              number: player.number != null ? String(player.number) : undefined,
            }),
          );
        }
      }

      // Costruisco le size per questo prodotto
      const sizes: BulkSizeUpdateSizeRequest[] = [];
      sizeMap.forEach(({ selectedSize, totalRequestedQuantity, players }) => {
        const isUpdate = selectedSize.id != null && Number(selectedSize.id) > 0;

        if (isUpdate) {
          sizes.push(new BulkSizeUpdateSizeRequest({
            customizedProductSizeId: Number(selectedSize.id),
            sizeId: Number(selectedSize.sizeId),
            requestedQuantity: totalRequestedQuantity,
            players,
          }));
        } else {
          sizes.push(new BulkSizeUpdateSizeRequest({
            customizedProductSizeId: null,
            sizeId: Number(selectedSize.sizeId),
            variantId: Number(selectedSize.variantId),
            sizeName: selectedSize.sizeName,
            sizeGroup: this.convertNumericSizeGroupToLabel(selectedSize.sizeGroup),
            sizeOrder: selectedSize.sizeOrder,
            wholesaleUnitPrice: selectedSize.wholesaleUnitPrice,
            requestedQuantity: totalRequestedQuantity,
            players,
          }));
        }
      });

      products.push(new BulkSizeUpdateProductRequest({
        customizedProductId: productId,
        sizes,
      }));
    }

    return new BulkCustomizedProductSizesUpdateRequest({ products });
  }

  /**
   * Costruisce il payload per il bulk update a partire dai prodotti completi (con tutte le size)
   * e dalla mappa player. Usato nel flusso di approvazione commerciale.
   *
   * A differenza di `buildBulkSizeUpdatePayload`:
   * - Itera su TUTTE le size dei prodotti (non solo quelle con player)
   * - Usa la requestedQuantity totale della size (size.requestedQuantity + player qty)
   * - Garantisce che size senza player non vengano cancellate dalla replace-all semantics
   */
  private buildBulkSizePayloadFromProducts(
    crProducts: CustomizedProductVM[],
    playersByProduct: { [productId: number]: PlayerCustomizationVM[] },
  ): BulkCustomizedProductSizesUpdateRequest {
    const products: BulkSizeUpdateProductRequest[] = [];

    for (const product of crProducts) {
      const sizes: BulkSizeUpdateSizeRequest[] = [];

      for (const size of product.sizes ?? []) {
        // Quantità player per questa variante (somma dei requestedQuantity dei singoli player)
        const playerQty = this.getPlayerQuantityForVariant(product.id, size.sizeId, playersByProduct);
        // Quantità totale = base size + player
        const totalQty = (size.requestedQuantity || 0) + playerQty;

        // Skippa size con quantità totale 0 (non ha senso inviarle)
        if (totalQty <= 0) continue;

        // Costruisce la lista di identità player distinte per questa variante
        const playerIdentities = this.buildDistinctPlayerIdentities(product.id, size.sizeId, playersByProduct);

        const isUpdate = size.id != null && Number(size.id) > 0;

        if (isUpdate) {
          sizes.push(new BulkSizeUpdateSizeRequest({
            customizedProductSizeId: Number(size.id),
            sizeId: Number(size.sizeId),
            requestedQuantity: totalQty,
            players: playerIdentities,
          }));
        } else {
          // INSERT: tutti i campi catalogo obbligatori
          sizes.push(new BulkSizeUpdateSizeRequest({
            customizedProductSizeId: null,
            sizeId: Number(size.sizeId),
            variantId: Number(size.variantId),
            sizeName: size.sizeName,
            sizeGroup: this.convertNumericSizeGroupToLabel(size.sizeGroup),
            sizeOrder: size.sizeOrder,
            wholesaleUnitPrice: size.wholesaleUnitPrice,
            requestedQuantity: totalQty,
            players: playerIdentities,
          }));
        }
      }

      // Includi sempre il prodotto nel payload, anche con sizes: [].
      // L'endpoint fa replace-all per prodotto: se sizes è vuoto, le sizes esistenti vengono cancellate.
      products.push(new BulkSizeUpdateProductRequest({
        customizedProductId: product.id,
        sizes,
      }));
    }

    return new BulkCustomizedProductSizesUpdateRequest({ products });
  }

  /**
   * Costruisce l'array di player per una specifica variante (prodotto + taglia).
   * Ogni player viene ripetuto N volte in base alla sua requestedQuantity.
   */
  private buildDistinctPlayerIdentities(
    productId: number,
    sizeId: number,
    playersByProduct: { [productId: number]: PlayerCustomizationVM[] },
  ): BulkSizeUpdatePlayerRequest[] {
    const players = playersByProduct?.[productId] ?? [];
    const identities: BulkSizeUpdatePlayerRequest[] = [];

    for (const player of players) {
      const prod = player.products?.find(p => p.id === productId && p.selectedSize?.sizeId === sizeId);
      if (!prod?.selectedSize || !(prod.selectedSize.requestedQuantity > 0)) continue;

      const qty = prod.selectedSize.requestedQuantity;
      for (let i = 0; i < qty; i++) {
        identities.push(new BulkSizeUpdatePlayerRequest({
          displayName: player.displayName ?? '',
          firstName: player.firstName ?? undefined,
          lastName: player.lastName ?? undefined,
          number: player.number != null ? String(player.number) : undefined,
        }));
      }
    }

    return identities;
  }

  /**
   * Converte il valore numerico di SizeGroupTypeEnum nella label stringa
   * richiesta dall'API (es. 0 → "Adult", 1 → "Youth", ecc.)
   */
  private convertNumericSizeGroupToLabel(numericValue: number): SizeGroupLabel {
    const mapping: Record<number, SizeGroupLabel> = {
      [SizeGroupTypeEnum.adult]: 'Adult',
      [SizeGroupTypeEnum.youth]: 'Youth',
      [SizeGroupTypeEnum.kid]: 'Kid',
      [SizeGroupTypeEnum.infant]: 'Infant',
    };
    return mapping[numericValue] ?? 'Adult';
  }

  getGraphicalApproval(loaderId: string, customRequestId: number) {
    return this._httpClient.get<GraphicalApprovalResponseDto>(
      `${this.baseUrl}/customization-requests/${customRequestId}/graphical-approval`,
      null,
      loaderId,
    );
  }

  approveCustomizationRequestAsGraphical(
    loaderId: string,
    customRequestId: number,
    crProducts: CustomizedProductVM[],
    reason?: TemporaryFileReason,
  ) {
    // Upload dei documenti PDF (obbligatori)
    const documentRequests: HttpBasicRequest[] = crProducts.map((x) => {
      const formData = new FormData();
      formData.append("file", x.graphicalApprovalDocument);
      return new HttpBasicRequest({
        body: formData,
        method: "post",
        options: null,
        url: `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.GraphicalApprovalDocument}`,
      });
    });

    // Upload delle immagini custom thumbnail (opzionali)
    const imageRequests: HttpBasicRequest[] = crProducts.map((x) => {
      if (x.graphicalApprovalImage) {
        const formData = new FormData();
        formData.append("file", x.graphicalApprovalImage);
        return new HttpBasicRequest({
          body: formData,
          method: "post",
          options: null,
          url: `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileReason.CustomizationRequestGraphicalApprovalImage}`,
        });
      }
      return null;
    });

    return this._httpClient.forkJoin<string>(documentRequests, loaderId).pipe(
      switchMap((documentIds: string[]) => {
        // Upload immagini solo per i prodotti che le hanno
        const validImageRequests = imageRequests.filter((r) => r !== null);
        if (validImageRequests.length === 0) {
          return this._submitGraphicalApproval(
            customRequestId,
            crProducts,
            documentIds,
            [],
            loaderId,
          );
        }

        return this._httpClient
          .forkJoin<string>(validImageRequests, loaderId)
          .pipe(
            switchMap((imageIds: string[]) => {
              // Mappa gli imageIds ai prodotti corretti
              let imageIdIndex = 0;
              const mappedImageIds = crProducts.map((p) => {
                if (p.graphicalApprovalImage) {
                  return imageIds[imageIdIndex++];
                }
                return null;
              });
              return this._submitGraphicalApproval(
                customRequestId,
                crProducts,
                documentIds,
                mappedImageIds,
                loaderId,
              );
            }),
          );
      }),
    );
  }

  private _submitGraphicalApproval(
    customRequestId: number,
    crProducts: CustomizedProductVM[],
    documentIds: string[],
    imageIds: (string | null)[],
    loaderId: string,
  ) {
    const body: GraphicalApprovalDTI = new GraphicalApprovalDTI({
      approvedProducts: crProducts.map(
        (product, i) =>
          new GraphicalApprovalApprovedProductDTI({
            approvedColorCode: product.approveColorCode,
            approvedColorName: product.approveColorName,
            customizedProductId: product.id,
            temporaryDocumentAttachmentUploadId: documentIds[i],
            temporaryImageAttachmentUploadId: imageIds[i] || undefined,
          }),
      ),
    });
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/graphical-approval`,
      body,
      null,
      loaderId,
    );
  }

  getCommercialApproval(loaderId: string, customRequestId: number) {
    return this._httpClient.get<CommercialApprovalResponseDto>(
      `${this.baseUrl}/customization-requests/${customRequestId}/commercial-approval`,
      null,
      loaderId,
    );
  }

  approveCustomizationRequestAsCommercial(
    loaderId: string,
    customRequestId: number,
    crProducts: CustomizedProductVM[],
    playersByProduct: { [productId: number]: PlayerCustomizationVM[] },
    approvedDeliveryDate: string,
    approvedFinancialDiscountPercentage: number,
    personalizedCost: number,
    shippingCost: number,
  ) {
    const body = new CommercialApprovalDTI({
      approvedDeliveryDate,
      approvedFinancialDiscountPercentage,
      personalizedCost,
      shippingCost,
      approvedProducts: crProducts.map(
        (product) =>
          new CommercialApprovalApprovedProductDTI({
            approvedVariants: product.sizes
              .filter((size) => {
                // Quantità totale = size qty + player qty per questa variante
                const playerQty = this.getPlayerQuantityForVariant(product.id, size.sizeId, playersByProduct);
                return (size.requestedQuantity || 0) + playerQty > 0;
              })
              .map(
                (size) => {
                  // Calcola quantità player per questa variante
                  const playerQty = this.getPlayerQuantityForVariant(product.id, size.sizeId, playersByProduct);
                  // Costruisce array di PlayerCustomizationDTO dai player del form
                  const players = this.buildPlayerDTOsForVariant(product.id, size.sizeId, playersByProduct);

                  return new CommercialApprovalApprovedVariantDTI({
                    // Total pieces = quantità senza customizzazione + quantità con customizzazione
                    approvedQuantity: (size.requestedQuantity || 0) + playerQty,
                    playerCustomizations: players.length > 0 ? players : undefined,
                    variantId: size.variantId,
                  });
                },
              ),
            customizedProductId: product.id,
            personalizedCost
          }),
      ),
    });
    const hasAnyPlayers = Object.values(playersByProduct).some(arr => arr?.length > 0);

    const commercialPost$ = this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/commercial-approval`,
      body,
      null,
      loaderId,
    );

    if (!hasAnyPlayers) {
      return commercialPost$;
    }

    const bulkSizePayload = this.buildBulkSizePayloadFromProducts(crProducts, playersByProduct);

    return commercialPost$.pipe(
      switchMap(() =>
        this._httpClient.post<BulkCustomizedProductSizesUpdateResponse>(
          `${this.baseUrl}/customization-requests/${customRequestId}/products/sizes`,
          bulkSizePayload,
          null,
          loaderId,
        ),
      ),
    );
  }

  /**
   * Calcola la quantità totale di player per una specifica variante (prodotto + taglia).
   */
  private getPlayerQuantityForVariant(
    productId: number,
    sizeId: number,
    playersByProduct: { [productId: number]: PlayerCustomizationVM[] },
  ): number {
    const players = playersByProduct?.[productId] ?? [];
    let qty = 0;
    for (const player of players) {
      const prod = player.products?.find(p => p.id === productId && p.selectedSize?.sizeId === sizeId);
      if (prod?.selectedSize) {
        qty += prod.selectedSize.requestedQuantity || 0;
      }
    }
    return qty;
  }

  /**
   * Costruisce l'array di PlayerCustomizationDTO per una specifica variante (prodotto + taglia).
   * Ogni player viene ripetuto N volte in base alla sua requestedQuantity (stessa logica del flusso requester).
   */
  private buildPlayerDTOsForVariant(
    productId: number,
    sizeId: number,
    playersByProduct: { [productId: number]: PlayerCustomizationVM[] },
  ): PlayerCustomizationDTO[] {
    const players = playersByProduct?.[productId] ?? [];
    const result: PlayerCustomizationDTO[] = [];
    for (const player of players) {
      const prod = player.products?.find(p => p.id === productId && p.selectedSize?.sizeId === sizeId);
      if (prod?.selectedSize) {
        const count = prod.selectedSize.requestedQuantity || 0;
        for (let i = 0; i < count; i++) {
          result.push(new PlayerCustomizationDTO({
            displayName: player.displayName,
            firstName: player.firstName,
            lastName: player.lastName,
            number: player.number,
            id: player.id,
          }));
        }
      }
    }
    return result;
  }

  getCustomizationRequestTimeline(loaderId: string, customRequestId: number) {
    return this._httpClient.get<RequestTimelineResponseDto>(
      `${this.baseUrl}/customization-requests/${customRequestId}/timeline`,
      null,
      loaderId,
    );
  }

  // postMessageOnTimeline(loaderId: string, customRequestId: number, message: string, isOnlyForInternal: boolean, isInternalUser: boolean, files: File, selectedProductId: number = null) {
  //   const body = {
  //     messageContent: message

  //   };

  //   if (isInternalUser) body['isVisibleForRequestingUser'] = !isOnlyForInternal;
  //   if (selectedProductId) body['customizedProductId'] = selectedProductId;

  //   const formData = new FormData();
  //   formData.append('file', files);

  //   return new Observable(observer => {
  //     if (files) {
  //       this._httpClient.post(`${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`, formData, null, loaderId).subscribe(res => {
  //         body['temporaryAttachmentUploadId'] = res;
  //         observer.next();
  //       });
  //     } else {
  //       observer.next();
  //     }
  //   }).pipe(
  //     switchMap(() => this._httpClient.post(`${this.baseUrl}/customization-requests/${customRequestId}/messages`, body, null, loaderId)
  //     ));
  // }

  postMessageOnTimeline(
    loaderId: string,
    customRequestId: number,
    message: string,
    isOnlyForInternal: boolean,
    isInternalUser: boolean,
    files: File[],
    selectedProductId: number = null,
  ) {
    const body = {
      messageContent: message,
    };

    if (isInternalUser) body["isVisibleForRequestingUser"] = !isOnlyForInternal;
    if (selectedProductId) body["customizedProductId"] = selectedProductId;

    return new Observable((observer) => {
      if (files && files.length > 0) {
        const uploadRequests: HttpBasicRequest[] = files.map((file) => {
          const formData = new FormData();
          formData.append("file", file);
          return new HttpBasicRequest({
            method: "post",
            url: `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`,
            body: formData,
          });
        });
        this._httpClient.forkJoin<any>(uploadRequests, loaderId).subscribe({
          next: (res) => {
            body["temporaryAttachmentUploadIds"] = res;
            observer.next(body);
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
      } else {
        observer.next(null);
      }
    }).pipe(
      switchMap(() => {
        return this._httpClient.post(
          `${this.baseUrl}/customization-requests/${customRequestId}/messages`,
          body,
          null,
          loaderId,
        );
      }),
    );
  }

  downloadGraphicApprovalDocument(
    loaderId: string,
    customRequestId: number,
    productId: number,
  ) {
    this._httpClient
      .getBlob(
        `${this.baseUrl}/customization-requests/${customRequestId}/graphical-approval/${productId}/document`,
        null,
        loaderId,
      )
      .subscribe((res) =>
        UtilityFunctions.downloadFile(res.blob, res.fileName),
      );
  }

  downloadGraphicApprovalDocumentZip(loaderId: string, customRequestId: number) {
    this._httpClient
      .postBlob(
        `${this.baseUrl}/customization-requests/${customRequestId}/graphical-approval/documents/download`,
        {},
        null,
        loaderId,
      )
      .subscribe((res) =>
        UtilityFunctions.downloadFile(res.blob, res.fileName),
      );
  }

  getListOfAttachedImages(loaderId: string, customRequestId: number) {
    return this._httpClient.get<MessageAttachmentResponseDto[]>(
      `${this.baseUrl}/customization-requests/${customRequestId}/attachments`,
      null,
      loaderId,
    );
  }

  getAttachmentByUrlBlob(loaderId: string, url: string) {
    return this._httpClient.getBlob(url, {}, loaderId).pipe(map((x) => x.blob));
  }

  downloadTimelineAttachment(
    loaderId: string,
    customRequestId: number,
    attachmentId: number,
  ) {
    this._httpClient
      .getBlob(
        `${this.baseUrl}/customization-requests/${customRequestId}/attachments/${attachmentId}`,
        null,
        loaderId,
      )
      .subscribe((res) =>
        UtilityFunctions.downloadFile(res.blob, res.fileName),
      );
  }

  bulkTemporaryUploads(
    loaderId: string,
    files: Array<{ blob: Blob; fileName: string }>,
  ) {
    const requests: HttpBasicRequest[] = files.map((file) => {
      const formData = new FormData();
      formData.append("file", file.blob, file.fileName);
      return new HttpBasicRequest({
        method: "post",
        url: `${this.baseUrl}/temporary-uploads?reason=${TemporaryFileTypeEnum.CustomizationRequestMessageAttachment}`,
        body: formData,
      });
    });

    return this._httpClient.forkJoin<string>(requests, loaderId);
  }

  bulkAddMediaToCustomizationRequest(
    loaderId: string,
    customRequestId: number,
    mediaIds: string[],
  ) {
    // const requests: HttpBasicRequest[] = mediaIds.map(x => new HttpBasicRequest({
    //   method: 'post',
    //   url: `${this.baseUrl}/customization-requests/${customRequestId}/media/${x}`,
    // }));
    // return this._httpClient.forkJoin(requests, loaderId);
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${customRequestId}/media`,
      mediaIds,
      null,
      loaderId,
    );
  }

  getReportOfCustomizationRequest(loaderId: string, customRequestId: number) {
    return this._httpClient.getBlob(
      `${this.baseUrl}/customization-requests/${customRequestId}/pdf`,
      null,
      loaderId,
    );
  }

  getReportOfCustomizationRequestProducts(
    loaderId: string,
    customRequestId: number,
    productId: number,
  ) {
    return this._httpClient.getBlob(
      `${this.baseUrl}/customization-requests/${customRequestId}/product/${productId}/pdf`,
      null,
      loaderId,
    );
  }

  updateCustomizedProductName(
    loaderId: string,
    requestId: number,
    customizedProductId: number,
    comments: string,
  ) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${requestId}/products/${customizedProductId}/details`,
      { comments },
      null,
      loaderId,
    );
  }
  getTranslatedMessage(
    loaderId: string,
    customRequestId: number,
    logEntryId: number,
  ): Observable<string> {
    return this._httpClient.get(
      `${this.baseUrl}/customization-requests/${customRequestId}/timeline/${logEntryId}/translate`,
      { responseType: "text" },
      loaderId,
    );
  }

  duplicateCustomizationRequest(loaderId: string, requestId: number) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${requestId}/duplicate`,
      {},
      null,
      loaderId,
    );
  }

  revertToDraft(loaderId: string, requestId: number) {
    return this._httpClient.put(
      `${this.baseUrl}/customization-requests/${requestId}/revert-to-draft`,
      {},
      null,
      loaderId,
    );
  }

  notifyDesignerReady(loaderId: string, requestId: number) {
    return this._httpClient.post(
      `${this.baseUrl}/customization-requests/${requestId}/notify-designer-ready`,
      {},
      null,
      loaderId,
    );
  }

  downloadExcelPlayers(loaderId: string, requestId: number) {
    return this._httpClient.getBlob(
      `${this.baseUrl}/customization-requests/${requestId}/players-excel`,
      null,
      loaderId,
    );
  }

  // ============================================================
  // IMPORT GIOCATORI/VARIANTI DA EXCEL
  // Vedi cr-excel-import.models.ts per il contratto
  // ============================================================

  /**
   * Scarica il template Excel della richiesta, precompilato con prodotti, taglie e giocatori
   * gia' presenti: vale anche da export dello stato corrente.
   * Disponibile solo dopo l'approvazione grafica (serve il colore approvato).
   */
  downloadPlayersImportTemplate(loaderId: string, requestId: number) {
    return this._httpClient.getBlob(
      `${this.baseUrl}/customization-requests/${requestId}/players-import-template`,
      null,
      loaderId,
    );
  }

  /**
   * Manda il file compilato e ne riceve lo stato con cui riempire il form. Non persiste niente:
   * il salvataggio passa poi da `bulkUpdateProductSizes` o `approveCustomizationRequestAsRequester`.
   *
   * `skipErrorHandling` disattiva il toast generico dell'interceptor: gli errori di questo
   * endpoint sono messaggi per l'utente (righe da correggere, colore non approvato) e vanno
   * mostrati dentro la modale, non in un toast con Method/Url/Status.
   */
  previewPlayersImport(
    loaderId: string,
    requestId: number,
    file: File,
  ): Observable<PlayersImportPreviewDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this._httpClient.post<PlayersImportPreviewDto>(
      `${this.baseUrl}/customization-requests/${requestId}/players-import-preview`,
      formData,
      new HttpBasicRequestOptions({ headers: { skipErrorHandling: 'true' } }),
      loaderId,
    );
  }

  downloadReportExcel(loaderId: string, params: CrReportExcelParams) {
    const queryParams: Params = { storeId: params.storeId.toString() };

    if (params.channelId) queryParams['channelId'] = params.channelId.toString();
    if (params.customerId) queryParams['customerId'] = params.customerId.toString();
    if (params.pointOfSaleId) queryParams['pointOfSaleId'] = params.pointOfSaleId.toString();
    if (params.status != null) queryParams['status'] = params.status.toString();
    if (params.commercialApprovalByUserId) queryParams['commercialApprovalByUserId'] = params.commercialApprovalByUserId.toString();
    if (params.createdByUserId) queryParams['createdByUserId'] = params.createdByUserId.toString();
    if (params.designerUserId) queryParams['designerUserId'] = params.designerUserId.toString();
    if (params.customerRequestingUserId) queryParams['customerRequestingUserId'] = params.customerRequestingUserId.toString();
    if (params.agencyRequestingUserId) queryParams['agencyRequestingUserId'] = params.agencyRequestingUserId.toString();
    if (params.agencyId) queryParams['agencyId'] = params.agencyId.toString();
    if (params.searchString) queryParams['searchString'] = params.searchString;

    return this._httpClient.getBlob(
      `${this.baseUrl}/customization-requests/report-excel`,
      { params: queryParams },
      loaderId,
    );
  }
}
